import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react';
import { apiFetch } from '../../lib/api';

interface AgentLogEntry {
  _id: string;
  timestamp: string;
  projectId: string;
  type: 'status' | 'tool_use' | 'thinking' | 'output' | 'error' | 'milestone';
  tool?: string;
  summary: string;
}

interface Project {
  _id: string;
  title: string;
  status: string;
  template: string;
  progress: {
    totalTasks: number;
    completedTasks: number;
    currentTask: string;
    testResults: Array<{ passed: number; failed: number; timestamp: string }>;
    retryCount: number;
  };
  totalCostUsd: number;
  repoUrl?: string;
  createdAt: string;
  completedAt?: string;
}

const DEV_ENGINE_URL = '/dev-engine';

/** これ以外は終了状態。ポーリングを止める判定に使う。 */
const ACTIVE_STATUSES = ['queued', 'scaffolding', 'developing', 'testing', 'fixing'];

const LOG_TYPE_STYLES: Record<string, string> = {
  status: 'text-blue-600',
  tool_use: 'text-cyan-600',
  thinking: 'text-gray-500',
  output: 'text-green-600',
  error: 'text-red-600',
  milestone: 'text-yellow-600 font-semibold',
};

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [logs, setLogs] = useState<AgentLogEntry[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);
  /** 取得済みの最後の _id。差分だけ取りに行くためのカーソル。 */
  const sinceRef = useRef<string | null>(null);
  /** ポーリングが重ならないようにする。3 秒より遅い応答が返ると
   *  次の周期が同じカーソルで走り、同じ行を二重に描画してしまう。 */
  const inFlightRef = useRef(false);
  /** 終了後に 1 回だけ取りに行ったか。最後の数行を取りこぼさないため、
   *  「終了を検知したら即停止」ではなく「終了後にもう 1 回引いてから停止」。 */
  const finalPullDoneRef = useRef(false);
  const statusRef = useRef<string | undefined>(undefined);

  // Fetch project data
  const { data: project, isLoading } = useQuery<Project>({
    queryKey: ['project', id],
    queryFn: async () => {
      const res = await apiFetch(`${DEV_ENGINE_URL}/projects/${id}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    enabled: !!id,
    // 終わった案件のページを開きっぱなしにしても叩き続けない。
    refetchInterval: (query) =>
      ACTIVE_STATUSES.includes((query.state.data as Project | undefined)?.status ?? '') ? 3000 : false,
  });

  statusRef.current = project?.status;
  if (statusRef.current && ACTIVE_STATUSES.includes(statusRef.current)) {
    // 再開発で走り直したらまた追いかける
    finalPullDoneRef.current = false;
  }

  // ログは DB から差分ポーリングで取る。
  //
  // 以前は WebSocket で受けて React の state にだけ積んでいたため、
  // 画面をリロードすると全部消えていた。購読前に出た分も届かない。
  // サーバー側で永続化したので、リロードしても最初から読み直せる。
  //
  // 再接続の心配が要らなくなったのも利点で、旧実装の onclose は
  // 「3 秒後に再接続」と書いてありながら参照を null にするだけで
  // 実際には再接続していなかった。
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    // 別のプロジェクトに切り替わったら積んだログとカーソルを捨てる。
    // 残すと前のプロジェクトの行に続けて表示され、しかもカーソルが
    // 進んでいるぶん新しい方の古い行が飛ぶ。
    sinceRef.current = null;
    inFlightRef.current = false;
    finalPullDoneRef.current = false;
    setLogs([]);

    const pull = async () => {
      if (inFlightRef.current) return;
      const settled = Boolean(statusRef.current) && !ACTIVE_STATUSES.includes(statusRef.current!);
      if (settled && finalPullDoneRef.current) return;
      if (settled) finalPullDoneRef.current = true;
      inFlightRef.current = true;
      try {
        const since = sinceRef.current;
        const url = `${DEV_ENGINE_URL}/projects/${id}/logs${since ? `?since=${encodeURIComponent(since)}` : ''}`;
        const res = await apiFetch(url);
        if (!res.ok || cancelled) return;
        const { logs: batch } = (await res.json()) as { logs: AgentLogEntry[]; truncated: boolean };
        if (cancelled || batch.length === 0) return;
        sinceRef.current = batch[batch.length - 1]._id;
        setLogs((prev) => [...prev, ...batch]);
      } catch {
        // 一度の失敗でポーリングを止めない。次の周期で取り直す。
      } finally {
        inFlightRef.current = false;
      }
    };

    pull();
    const timer = setInterval(pull, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id]);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!project) {
    return <div className="container p-6 text-center">Project not found</div>;
  }

  const isActive = ACTIVE_STATUSES.includes(project.status);

  return (
    <div className="container h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/projects">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{project.title}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                project.status === 'completed' ? 'bg-green-100 text-green-700'
                : project.status === 'failed' ? 'bg-red-100 text-red-700'
                : 'bg-blue-100 text-blue-700'
              }`}>
                {project.status}
              </span>
              <span>{project.template}</span>
              <span>Cost: ${(project.totalCostUsd || 0).toFixed(4)}</span>
              {project.repoUrl && (
                <a
                  href={project.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> GitHub
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Progress */}
        {project.progress.totalTasks > 0 && (
          <div className="rounded-lg border bg-card p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">{project.progress.currentTask || 'Idle'}</span>
              <span>{project.progress.completedTasks}/{project.progress.totalTasks} tasks</span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  project.status === 'completed' ? 'bg-green-500'
                  : project.status === 'failed' ? 'bg-red-500'
                  : 'bg-blue-500'
                }`}
                style={{ width: `${Math.round((project.progress.completedTasks / project.progress.totalTasks) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Test Results */}
        {project.progress.testResults?.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <h3 className="font-semibold mb-2">Test Results</h3>
            <div className="space-y-1 text-sm">
              {project.progress.testResults.map((tr, i) => (
                <div key={i} className="flex gap-4">
                  <span className="text-muted-foreground">{new Date(tr.timestamp).toLocaleTimeString()}</span>
                  <span className="text-green-600">{tr.passed} passed</span>
                  <span className={tr.failed > 0 ? 'text-red-600' : 'text-green-600'}>{tr.failed} failed</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Real-time Logs */}
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-4 py-2">
            <h3 className="font-semibold">Agent Logs</h3>
            {isActive && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="h-96 overflow-y-auto p-4 font-mono text-xs space-y-1 bg-gray-950 text-gray-200">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                {isActive ? 'Waiting for logs...' : 'No logs'}
              </div>
            ) : (
              logs.map((log, i) => (
                <div key={i} className={LOG_TYPE_STYLES[log.type] || 'text-gray-300'}>
                  <span className="text-gray-600">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                  {log.tool && <span className="text-cyan-400"> [{log.tool}]</span>}
                  {' '}{log.summary}
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
